import React, { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { TransactionFilters } from '../components/TransactionFilters';
import { ProcessingDetailsPanel } from '../components/ProcessingDetailsPanel';
import { LoadingScreen } from '../components/LoadingScreen';
import { Pagination } from '../components/Pagination';
import { DocumentUploadButton } from '../components/DocumentUploadButton';
import { DocumentDialog } from '../components/DocumentDialog';
import { ApprovalAPI } from '../services/api';
import { PendingSale, UnifiedTransaction } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { AlertService } from '../services/AlertService';
import { useTransactionFilters, toBackendFilters } from '../hooks/useTransactionFilters';

type Variant = 'pending' | 'processed' | 'terminal';

interface MyProcessingProps {
  variant: Variant;
}

const COPY: Record<Variant, { title: string; subtitle: string; emptyTitle: string; emptyBody: string }> = {
  pending: {
    title: 'My Pending Processing',
    subtitle: 'Your paid sales that are still being processed',
    emptyTitle: 'Nothing In Processing',
    emptyBody: "You don't have any sales currently being processed.",
  },
  processed: {
    title: 'My Processed',
    subtitle: 'Your paid sales that have completed processing',
    emptyTitle: 'Nothing Processed Yet',
    emptyBody: "You don't have any completed sales yet.",
  },
  terminal: {
    title: 'My Chargebacks & Reversals',
    subtitle: 'Your cancelled, charged-back, voided and refunded sales',
    emptyTitle: 'Nothing Here',
    emptyBody: "You don't have any cancelled, charged-back, voided or refunded sales.",
  },
};

// Terminal outcome label + badge class, derived from the linked transaction's
// void/refund flags first, then the processing status.
const terminalOutcome = (sale: any): { label: string; cls: string } => {
  if (sale.is_voided) return { label: 'VOIDED', cls: 'proc-voided' };
  if (sale.is_refunded) return { label: 'REFUNDED', cls: 'proc-refunded' };
  if (sale.processing_status === 'CHARGEBACK') return { label: 'CHARGEBACK', cls: 'proc-chargeback' };
  if (sale.processing_status === 'CANCELLED') return { label: 'CANCELLED', cls: 'proc-cancelled' };
  return { label: (sale.processing_status || 'UNKNOWN').replace(/_/g, ' '), cls: 'proc-cancelled' };
};

export const MyProcessing: React.FC<MyProcessingProps> = ({ variant }) => {
  const { user } = useAuth();
  const copy = COPY[variant];
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  // Documents are keyed by pending_sale_id on the backend; passing the row's id
  // (the PendingSale id) resolves to the SAME record Paid Transactions uses, so
  // docs stay in sync across pages.
  const [documentDialogRecordId, setDocumentDialogRecordId] = useState<string | null>(null);
  const [allSales, setAllSales] = useState<PendingSale[]>([]);
  // True totals over the full dataset (server aggregate), independent of the page.
  const [stats, setStats] = useState<{ total: number; total_amount: number }>({ total: 0, total_amount: 0 });

  // The fetched page rows, shaped for the shared filter bar (which derives its
  // dropdown option lists from these). Filtering itself happens SERVER-SIDE via
  // `toBackendFilters(filters)` below — matching Pending Processing / Paid Transactions.
  const mappedRows = useMemo<UnifiedTransaction[]>(() => allSales.map(sale => ({
    ...sale,
    tx_type: 'sale',
    nmi_response_code: sale.preauth_response_code,
    nmi_response_text: sale.preauth_response_text,
    status: sale.status,
    created_by_name: sale.created_by,
  })) as unknown as UnifiedTransaction[], [allSales]);

  // We only need the filter STATE from the hook — its client-side filtering is
  // bypassed (the server already returns the filtered page).
  const { filters, updateFilter, clearFilters, hasActiveFilters } = useTransactionFilters(mappedRows);

  // Counts reflect the full filtered dataset (server aggregate), not just this page.
  const filterStats = { total: stats.total, filtered: stats.total, hidden: 0 };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        offset: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        filters: toBackendFilters(filters),
      };
      const res = variant === 'pending'
        ? await ApprovalAPI.getMyPendingProcessingPaged(params)
        : variant === 'processed'
        ? await ApprovalAPI.getMyProcessedPaged(params)
        : await ApprovalAPI.getMyTerminalSalesPaged(params);
      setAllSales(res.items);
      setStats({ total: res.total, total_amount: res.total_amount });
    } catch (error: any) {
      console.error('Failed to load processing sales:', error);
      AlertService.error(error.message || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  // Refetch the server page on paging / filter change / variant switch.
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filters, variant, user?.role]);

  // Reset to first page when filters or the variant change.
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, variant]);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (n: number) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateString?: string) =>
    dateString ? new Date(dateString).toLocaleString() : 'N/A';

  const getProcessingBadge = (status?: string) => {
    const label = (status || 'PENDING_ASSIGNMENT').replace(/_/g, ' ');
    const cls: Record<string, string> = {
      PENDING_ASSIGNMENT: 'proc-pending',
      ASSIGNED: 'proc-assigned',
      IN_PROCESS: 'proc-inprocess',
      DOCUMENTS_REQUIRED: 'proc-docs',
      COMPLETED: 'proc-completed',
      CANCELLED: 'proc-cancelled',
      CHARGEBACK: 'proc-cancelled',
    };
    return (
      <span className={`proc-badge ${cls[status || 'PENDING_ASSIGNMENT'] || 'proc-pending'}`}>
        {label.toUpperCase()}
      </span>
    );
  };

  const convertToTransaction = (sale: PendingSale): UnifiedTransaction => ({
    ...sale,
    tx_type: 'sale',
    nmi_response_code: sale.preauth_response_code,
    nmi_response_text: sale.preauth_response_text,
    created_by_name: sale.created_by,
  } as unknown as UnifiedTransaction);

  const handleViewDetails = (sale: PendingSale) => {
    setSelectedTransaction(convertToTransaction(sale));
    setShowDetailsPanel(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransaction(null);
  };

  return (
    <DashboardLayout>
      <div className="my-processing">
        <div className="page-header">
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>

        <TransactionFilters
          filters={filters}
          updateFilter={updateFilter}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          transactions={mappedRows}
          filterStats={filterStats}
        />

        <LoadingScreen isVisible={loading} message={`Loading ${copy.title.toLowerCase()}...`} />

        {!loading && (
          <>
            <div className="processing-summary">
              <div className="summary-card">
                <div className="summary-number">{stats.total.toLocaleString()}</div>
                <div className="summary-label">
                  {variant === 'pending' ? 'In Processing' : variant === 'processed' ? 'Processed' : 'Total'}
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-number">{formatCurrency(stats.total_amount)}</div>
                <div className="summary-label">Total Amount</div>
              </div>
            </div>

            {allSales.length === 0 ? (
              <div className="no-data">
                <div className="no-data-icon">📋</div>
                <h3>{copy.emptyTitle}</h3>
                <p>{copy.emptyBody}</p>
              </div>
            ) : (
              <>
                <div className="processing-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>State</th>
                        <th>Category</th>
                        <th>{variant === 'terminal' ? 'Outcome' : 'Processing Status'}</th>
                        <th>{variant === 'processed' ? 'Completed' : 'Created'}</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSales.map((sale) => (
                        <tr key={sale.id}>
                          <td>
                            <div className="customer-info">
                              <div className="customer-name">{sale.first_name} {sale.last_name}</div>
                              <div className="customer-email">{sale.email}</div>
                            </div>
                          </td>
                          <td className="amount">{formatCurrency(sale.amount)}</td>
                          <td>{sale.sale_state || 'N/A'}</td>
                          <td>{sale.category || 'N/A'}</td>
                          <td>
                            {variant === 'terminal'
                              ? (() => { const o = terminalOutcome(sale); return <span className={`proc-badge ${o.cls}`}>{o.label}</span>; })()
                              : getProcessingBadge(sale.processing_status)}
                          </td>
                          <td className="date">
                            {variant === 'processed'
                              ? formatDate((sale as any).processing_completed_at || sale.created_at)
                              : formatDate(sale.created_at)}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <DocumentUploadButton
                                recordId={sale.id}
                                onClick={() => setDocumentDialogRecordId(sale.id)}
                              />
                              <button
                                onClick={() => handleViewDetails(sale)}
                                className="btn btn-outline-secondary btn-sm"
                                title="View Details"
                              >
                                📋 Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalItems={stats.total}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  pageSizeOptions={[10, 20, 30, 50, 100]}
                />
              </>
            )}
          </>
        )}

        <ProcessingDetailsPanel
          transaction={selectedTransaction}
          isOpen={showDetailsPanel}
          onClose={handleCloseDetails}
        />

        {documentDialogRecordId && (
          <DocumentDialog
            recordId={documentDialogRecordId}
            isOpen={true}
            onClose={() => setDocumentDialogRecordId(null)}
            recordTitle={(() => {
              const s = allSales.find(x => x.id === documentDialogRecordId);
              return s ? `${s.first_name || ''} ${s.last_name || ''}`.trim() : undefined;
            })()}
          />
        )}

        <style>{`
          .my-processing { padding: 2rem; }
          .page-header { margin-bottom: 2rem; }
          .page-header h2 { margin: 0 0 0.5rem 0; color: var(--text-primary); }
          .page-header p { margin: 0; color: var(--text-secondary); }

          .processing-summary { display: flex; gap: 1rem; margin-bottom: 2rem; }
          .summary-card {
            background: var(--bg-card);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
            padding: 1.5rem;
            text-align: center;
            flex: 1;
            max-width: 200px;
          }
          .summary-number { font-size: 2rem; font-weight: bold; color: var(--primary); margin-bottom: 0.5rem; }
          .summary-label { font-size: 0.875rem; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.5px; }

          .no-data {
            text-align: center;
            padding: 4rem 2rem;
            background: var(--bg-card);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
          }
          .no-data-icon { font-size: 4rem; margin-bottom: 1rem; }
          .no-data h3 { margin: 0 0 1rem 0; color: var(--text-primary); }
          .no-data p { margin: 0; color: var(--text-secondary); }

          .processing-table {
            background: var(--bg-card);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 2rem;
          }
          .processing-table table { width: 100%; border-collapse: collapse; }
          .processing-table th, .processing-table td {
            padding: 1rem; text-align: left; border-bottom: 1px solid var(--border-primary);
          }
          .processing-table th { background: var(--bg-secondary); font-weight: 600; color: var(--text-primary); }
          .processing-table tr:last-child td { border-bottom: none; }

          .action-buttons { display: flex; align-items: center; gap: 0.5rem; }
          .customer-info .customer-name { font-weight: 600; color: var(--text-primary); }
          .customer-info .customer-email { font-size: 0.875rem; color: var(--text-secondary); margin-top: 0.25rem; }
          .amount { font-weight: 600; color: var(--success); }
          .date { font-size: 0.875rem; color: var(--text-secondary); }

          .proc-badge {
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.7rem;
            font-weight: 600;
            letter-spacing: 0.3px;
            white-space: nowrap;
          }
          .proc-pending   { background: rgba(255,193,7,0.12);  color: #b8860b; border: 1px solid rgba(255,193,7,0.3); }
          .proc-assigned  { background: rgba(13,110,253,0.12); color: #0b5ed7; border: 1px solid rgba(13,110,253,0.3); }
          .proc-inprocess { background: rgba(13,202,240,0.12); color: #0aa2c0; border: 1px solid rgba(13,202,240,0.3); }
          .proc-docs      { background: rgba(255,140,0,0.12);  color: #d2691e; border: 1px solid rgba(255,140,0,0.3); }
          .proc-completed { background: rgba(40,167,69,0.12);  color: #157347; border: 1px solid rgba(40,167,69,0.3); }
          .proc-cancelled { background: rgba(108,117,125,0.15); color: #5c636a; border: 1px solid rgba(108,117,125,0.35); }
          .proc-chargeback{ background: rgba(220,53,69,0.12);  color: #b02a37; border: 1px solid rgba(220,53,69,0.3); }
          .proc-voided    { background: rgba(111,66,193,0.12); color: #59359a; border: 1px solid rgba(111,66,193,0.3); }
          .proc-refunded  { background: rgba(253,126,20,0.12); color: #c9600f; border: 1px solid rgba(253,126,20,0.3); }

          @media (max-width: 768px) {
            .my-processing { padding: 1rem; }
            .processing-summary { flex-direction: column; }
            .summary-card { max-width: none; }
            .processing-table { overflow-x: auto; }
            .processing-table table { min-width: 800px; }
          }
        `}</style>
      </div>
    </DashboardLayout>
  );
};
